import { type FC, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { type InfiniteData, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Pagination } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'
import premiumLock from 'assets/images/locked.png'
import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconStatsClose } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { ShowMore } from 'components/ShowMore/ShowMore'
import { addToast } from 'components/Toast/Toast'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { ThreeDotsTrigger } from 'components-ref/atoms'
import { LastPostInfo, PaymentModal } from 'components-ref/molecules'
import { Modal } from 'components-ref/molecules/modal/modal'
import { usePreviewContext } from 'context/previewContext'
import ImagePreview from 'features/Preview/ImagePreview'
import PreviewActions from 'features/Preview/PreviewActions/PreviewActions'
import VideoPlayer from 'features/VideoPlayer/VideoPlayer'
import { useRefetchUserProfile } from 'helpers/hooks/useRefetchUserProfile/useRefetchUserProfile'
import { extractMedia } from 'helpers/media'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { renderPostText } from 'helpers/postHelpers'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import { purchasePremiumPost } from 'services/endpoints/post'
import { useUserProfile } from 'store'
import { type IInfinitePage, type IPost, type IPostMediaPreview } from 'types/interfaces/ITypes'
import 'swiper/css/bundle'
interface Props {
  post: IPost
  selectedSlide?: number
  showControls: boolean
  isFirstPost: boolean
  hasNextPage: boolean
  minimizeFn: (fileIndex: number, type: string) => void
  toggleControls: () => void
  isLastPost?: boolean
  setIsOptionsNavOpen?: (val: boolean) => void
  onClose?: () => void
  isInViewport?: boolean
}
const SinglePostPreviewPremiumMedia: FC<Props> = ({
  post,
  selectedSlide = 0,
  showControls,
  hasNextPage,
  minimizeFn,
  toggleControls,
  isLastPost = false,
  setIsOptionsNavOpen,
  onClose,
  isInViewport = true
}) => {
  const [activeSlide, setActiveSlide] = useState<number | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<{
    method: 'card' | 'deposit'
    cardId: string | undefined
  }>({
    method: 'deposit',
    cardId: undefined
  })
  const [overlayVisible, setOverlayVisible] = useState(true)
  const [confirmBuyModalOpen, setConfirmBuyModalOpen] = useState<boolean>(false)
  const navigate = useNavigate()
  const userData = useUserProfile()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const swiperRef = useRef<SwiperClass | null>(null)
const {
  query,
  minimized,
  setCommentsActive,
  clearModal,
  setSelectedPostData
} = usePreviewContext()
  const { refetchUserProfile } = useRefetchUserProfile()
  const showOverlay = (
    e?: React.MouseEvent
  ) => {
    e?.stopPropagation?.()
    setOverlayVisible(true)
  }
  const handleClose = (
    e?: React.MouseEvent<
      HTMLDivElement,
      MouseEvent
    >
  ) => {
    e?.stopPropagation()
    if (onClose) {
      onClose()
      return
    }
    clearModal()
  }
  const purchasePost = useMutation(
    async (payload: {
      postId: string
      paymentMethod: 'wallet' | 'card'
      cardId: string | undefined
    }) => {
      return await purchasePremiumPost(
        payload
      )
    },
    {
      onSuccess: response => {
        const updatedPost =
          response?.data?.message?.post ??
          response?.data?.post ??
          response?.post ??
          response?.data?.data ??
          response?.data
        if (updatedPost?.id) {
          patchPurchasedPost(updatedPost)
        }
        refetchUserProfile()
      },
      onError: (resp: any) => {
        addToast(
          'error',
          resp?.response?.data?.message
        )
      }
    }
  )
  const patchPostInInfiniteCache = (
    cacheQuery: readonly any[],
    updatedPost: IPost
  ) => {
    queryClient.setQueryData<
      InfiniteData<IInfinitePage> | undefined
    >(
      cacheQuery,
      old => {
        if (!old) {
          return old
        }
        return {
          ...old,
          pages: old.pages.map(
            singleFeedPage => ({
              ...singleFeedPage,
              page: {
                ...singleFeedPage.page,
                data: {
                  ...singleFeedPage.page.data,
                  data:
                    singleFeedPage.page.data.data.map(
                      feedPost =>
                        feedPost.id ===
                        updatedPost.id
                          ? {
                              ...feedPost,
                              ...updatedPost,
                              is_purchased:
                                true,
                              photos_preview:
                                updatedPost.photos_preview ??
                                [],
                              videos_preview:
                                updatedPost.videos_preview ??
                                [],
                              photos:
                                updatedPost.photos ??
                                feedPost.photos ??
                                [],
                              videos:
                                updatedPost.videos ??
                                feedPost.videos ??
                                [],
                              sounds:
                                updatedPost.sounds ??
                                feedPost.sounds ??
                                []
                            }
                          : feedPost
                    )
                }
              }
            })
          )
        }
      }
    )
  }
  const patchPurchasedPost = (
    updatedPost: IPost
  ) => {
    patchPostInInfiniteCache(
      query,
      updatedPost
    )
    patchPostInInfiniteCache(
      [
        'getHomeFeed',
        1,
        'all'
      ],
      updatedPost
    )
    setSelectedPostData({
      ...post,
      ...updatedPost,
      is_purchased: true,
      photos_preview:
        updatedPost.photos_preview ?? [],
      videos_preview:
        updatedPost.videos_preview ?? [],
      photos:
        updatedPost.photos ??
        post.photos ??
        [],
      videos:
        updatedPost.videos ??
        post.videos ??
        [],
      sounds:
        updatedPost.sounds ??
        post.sounds ??
        []
    })
    setConfirmBuyModalOpen(false)
  }
  const handleBuy = () => {
    if (
      userData?.status ===
        'restricted' &&
      userData?.role === 'fan'
    ) {
      addToast(
        'error',
        'Your account is restricted.'
      )
      return
    }
    setConfirmBuyModalOpen(true)
  }
  const postFile = {
    userId: post.user_id,
    name: post.user?.display_name,
    text: post.body,
    createdAt: post.created_at,
    mentions: post.tags,
    media: extractMedia(post),
    minimizedName:
      post.user?.display_name,
    minimizedAvatar:
      post.user?.cropped_avatar?.url ??
      post.user?.avatar?.url ??
      placeholderAvatar
  }
  const previewFiles = [
    ...(post.photos_preview ?? []),
    ...(post.videos_preview ?? [])
  ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const noDefaultCard =
    userData?.default_card == null ||
    String(
      userData?.default_card
    ) === ''
  const noWalletDeposit =
    userData?.wallet_deposit == null ||
    Number(
      userData?.wallet_deposit
    ) <= 0
  const showAddCard = noDefaultCard && noWalletDeposit
  useLayoutEffect(() => {
    const swiper = swiperRef.current
    if (!swiper || swiper.destroyed) return
    const nextSlide = selectedSlide ?? 0
    if (swiper.activeIndex !== nextSlide) {
      swiper.slideTo(nextSlide, 0, false)
    }
    setActiveSlide(nextSlide)
  }, [selectedSlide])
  const renderPurchaseModal = () => {
    if (!confirmBuyModalOpen) {
      return null
    }
    return (
      <Modal
        onClose={() =>
          setConfirmBuyModalOpen(false)
        }
        title={
          showAddCard
            ? 'Add A Card'
            : 'Select Payment Method'
        }
        closeOnLayerClick={true}
        isLayerTransparent={false}
      >
        {showAddCard ? (
          <ConfirmModal
            customClass='confirmModal--add-card'
            confirmText='Add Card'
            onCancel={() =>
              setConfirmBuyModalOpen(
                false
              )
            }
            body={
              <div>
                <p className='add-card-title-spacing'>
                  You don&apos;t have a
                  saved card or wallet
                  funds. Please add a card
                  to continue.
                </p>
              </div>
            }
            confirmFn={() => {
              navigate(
                '/settings/general/wallet'
              )
              setConfirmBuyModalOpen(
                false
              )
            }}
          />
        ) : (
          <PaymentModal
            purchaseCb={() => {
              purchasePost.mutate({
                postId: post.id,
                paymentMethod:
                  paymentMethod.method as any,
                cardId:
                  paymentMethod.cardId
              })
            }}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            price={post.price!}
          />
        )}
      </Modal>
    )
  }
  const renderBuyButton = () => {
    return (
      <>
        <Button
          text={
            <p>
              {t('unlock')}{' '}
              <span className='post__cta__button__price'>
                ${post.price}
              </span>
            </p>
          }
          color='blue'
          font='mont-14-normal'
          height='3'
          width='fit'
          padding='2'
          customClass='post__cta__button'
          clickFn={handleBuy}
          disabled={purchasePost.isLoading}
        />
        {renderPurchaseModal()}
      </>
    )
  }
  const renderPreviewActions = () => {
    if (
      !showControls ||
      minimized
    ) {
      return null
    }
    return (
      <>
        <PreviewActions
          stats={{
            overallRevenue:
              post.overall_revenue,
            purchasesCount:
              post.purchases_count,
            purchasesSum:
              post.purchases_sum,
            tipsCount:
              post.tips_count,
            tipsSum:
              post.tips_sum,
            conversionRate:
              post.conversion_rate,
            uniqueImpressions:
              post.unique_impressions,
            views:
              post.views,
            uniqueViews:
              post.unique_views,
            price:
              post.price
          }}
          modelData={{
            postId:
              post.id,
            modelId:
              post.user.id,
            avatarSrc:
              post.user.avatar?.url ??
              post.user
                .cropped_avatar?.url ??
              placeholderAvatar
          }}
          query={query}
          postId={post.id}
          isLiked={post.isLiked}
          likes={post.likes_count}
          comments={post.comment_count}
          setCommentsActive={setCommentsActive}
        />
        <ShowMore
          userId={postFile.userId}
          name={postFile.name}
          time={postFile.createdAt}
          tags={[
            '#bowieblue',
            '#bluechowchow'
          ]}
          text={
            postFile.text
              ? postFile.text
              : ''
          }
          mentions={postFile.mentions}
        />
      </>
    )
  }
  const renderTopControls = () => {
    return (
      <>
        <div
          className='preview__close'
          onClick={handleClose}
        >
          {minimized ? (
            <IconStatsClose
              color='#A3A3A3'
            />
          ) : (
            <IconStatsClose />
          )}
        </div>
        {showControls && (
          <ThreeDotsTrigger
            onClick={(
              e: React.MouseEvent<
                HTMLDivElement,
                MouseEvent
              >
            ) => {
              e.stopPropagation()
              if (
                setIsOptionsNavOpen
              ) {
                setIsOptionsNavOpen(
                  true
                )
              }
            }}
          />
        )}
      </>
    )
  }
  return (
    <>
      <div className='preview__file--wrapper'>
        <Swiper
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper
            const nextSlide = selectedSlide ?? 0
            if (swiper.activeIndex !== nextSlide) {
              swiper.slideTo(nextSlide, 0, false)
            }
            setActiveSlide(nextSlide)
          }}
          initialSlide={selectedSlide ?? 0}
          style={{
            width: '100%',
            height: '100%'
          }}
          className={`myInnerSwiper ${
            !overlayVisible
              ? 'spm-playing'
              : ''
          }`}
          pagination={{
            dynamicBullets: true
          }}
          modules={[
            Pagination
          ]}
          observer={true}
          preventInteractionOnTransition={true}
          onSlideChange={(
            swiper: SwiperClass
          ) => {
            setActiveSlide(
              swiper.activeIndex
            )
            setOverlayVisible(true)
            emitter.emit(
              EventsEmitter.STOP_ALL_AUDIO_POSTS
            )
          }}
        >
          {previewFiles
            .filter(
              item =>
                item?.preview?.type !==
                'blur'
            )
            .map(
              (
                item:
                  IPostMediaPreview,
                index: number
              ) => {
                const isVideo =
                item.duration != null ||
                item.preview?.type === 'clip' ||
                (post.videos_preview ?? []).some(
                    video => video.id === item.id
                ) ||
                (
                    post.video_count > 0 &&
                    post.photo_count === 0
                )
                if (!isVideo) {
                  const orientation =
                    item.preview
                      ?.orientation
                      ? item.preview.orientation.toLowerCase()
                      : ''
                  const rawPreviewSource =
                    item.preview?.src
                  const previewSource =
                    rawPreviewSource
                      ? getCachedBlob(
                          rawPreviewSource
                        ) ??
                        rawPreviewSource
                      : null
                  const isMissingPreview =
                    !previewSource
                  return (
                    <SwiperSlide
                      key={index}
                      className={classNames(
                        `${
                          !showControls ||
                          minimized
                            ? 'swiper-no-swiping'
                            : ''
                        }`,
                        minimized &&
                          'minimized-image'
                      )}
                    >
                      {renderTopControls()}
                      {renderPreviewActions()}
                      <div
                        onClick={() => {
                          toggleControls()
                        }}
                        className={`preview__file ${orientation}`}
                      >
                        {minimized ? (
                          <div className='preview__minimized'>
                            {previewSource ? (
                              <img
                                className='preview__file__image preview__file__image--minimized'
                                src={previewSource}
                                alt={t('preview')}
                              />
                            ) : item.preview?.src ? (
                              <img className='preview__file__image preview__file__image--minimized' src={item.preview?.src} alt={t('preview')} />
                            ) : (
                              <LockedMinimized><img src={premiumLock} alt='' /></LockedMinimized>
                            )}
                            <div className='preview__minimized__content'>
                              <div className='preview__minimized__content--top'>
                                <div className='header__avatar'>
                                  <img
                                    src={postFile.minimizedAvatar}
                                    alt={t('placeholder')}
                                  />
                                </div>
                                <div className='header__name'>
                                  {postFile.minimizedName}
                                </div>
                              </div>
                              <div className='preview__minimized__content--bottom'>
                                <div className='content__text'>
                                  {renderPostText(
                                    postFile.text ??
                                      '',
                                    postFile.mentions
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
<MediaContainer
  $locked={isMissingPreview}
>
  {previewSource ? (
    <>
      <img
        className='preview__background'
        src={previewSource}
        alt={t('background')}
      />
      <ImagePreview
        src={previewSource}
      />
    </>
  ) : (
    <LockedContent>
      <img
        src={premiumLock}
        alt=''
      />
      <p className='locked-premium-text'>
        {t('premium')}
      </p>
    </LockedContent>
  )}
</MediaContainer>
                            <div
                              className={`post__cta ${
                                isMissingPreview
                                  ? 'post__cta--locked-preview'
                                  : ''
                              }`}
                            >
                              {renderBuyButton()}
                            </div>
                          </>
                        )}
                        {isLastPost &&
                          !hasNextPage && (
                            <LastPostInfo />
                          )}
                      </div>
                    </SwiperSlide>
                  )
                }
                if (isVideo) {
                  const rawThumbnailSource = item.active_thumb
                  const thumbnailSource = rawThumbnailSource ? getCachedBlob(rawThumbnailSource) ?? rawThumbnailSource : null
                  const rawPreviewSource = item.preview?.src ?? null
                  const rawVideoSource = item.preview?.type === 'clip' ? rawPreviewSource : null
                  const previewThumbSource = item.preview?.type === 'thumb' && rawPreviewSource
                    ? getCachedBlob(rawPreviewSource) ?? rawPreviewSource
                    : null
                  const posterSource = previewThumbSource ?? thumbnailSource
                  return (
                    <SwiperSlide
                      key={index}
                      className={classNames(
                        `${
                          !showControls ||
                          minimized
                            ? 'swiper-no-swiping'
                            : ''
                        }`,
                        minimized &&
                          'minimized-video'
                      )}
                    >
                      {renderTopControls()}
                      {renderPreviewActions()}
                      <div
                        className={`preview__file ${
                          item.preview
                            ?.orientation ??
                          ''
                        }`}
                      >
                        {minimized ? (
                          <div className='preview__minimized'>
                            {rawVideoSource ? (
                              <VideoPlayer
                                segment_data={item.aggregated_segment_data}
                                poster={thumbnailSource ?? undefined}
                                url={rawVideoSource}
                                showControls={true}
                                duration={item.duration}
                                minimized={minimized}
                                setShowControls={toggleControls}
                                isActive={!!isInViewport && index === activeSlide}
                                shouldTrackPlayed={true}
                                videoId={item.id}
                                videoUserId={post.user?.id}
                              />
                            ) : previewThumbSource ? (
                              <img className='preview__file__image preview__file__image--minimized' src={previewThumbSource} alt={t('preview')} />
                            ) : (
                              <LockedMinimized><img src={premiumLock} alt='' /></LockedMinimized>
                            )}
                            <div className='preview__minimized__content'>
                              <div className='preview__minimized__content--top'>
                                <div className='header__avatar'>
                                  <img
                                    src={postFile.minimizedAvatar}
                                    alt={t('placeholder')}
                                  />
                                </div>
                                <div className='header__name'>
                                  {postFile.minimizedName}
                                </div>
                              </div>
                              <div className='preview__minimized__content--bottom'>
                                <div className='content__text'>
                                  {renderPostText(
                                    postFile.text ??
                                      '',
                                    postFile.mentions
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : rawVideoSource ? (
                          <MediaContainer $locked={false}>
                            {thumbnailSource && (
                              <img
                                className='preview__background'
                                src={thumbnailSource}
                                alt={t('background')}
                              />
                            )}
                            <VideoPlayer
                              segment_data={item.aggregated_segment_data}
                              poster={thumbnailSource ?? undefined}
                              url={rawVideoSource}
                              showControls={showControls}
                              duration={item.duration}
                              minimized={minimized}
                              setShowControls={toggleControls}
                              isActive={!!isInViewport && index === activeSlide}
                              shouldTrackPlayed={true}
                              videoId={item.id}
                              videoUserId={post.user?.id}
                              onPlay={() => setOverlayVisible(false)}
                              onPause={() => setOverlayVisible(true)}
                              onEnded={() => setOverlayVisible(true)}
                            />
                            {!overlayVisible && (
                              <div
                                className='spm-hitbox'
                                onClick={showOverlay}
                              />
                            )}
                            {overlayVisible && (
                              <div className='post__cta'>
                                <p className='post__cta__text'>
                                  {t('premium')}
                                </p>
                                {renderBuyButton()}
                              </div>
                            )}
                          </MediaContainer>
) : previewThumbSource ? (
  <MediaContainer $locked={false}>
    <img
      className='preview__background'
      src={previewThumbSource}
      alt={t('background')}
    />

    <img
      className='preview__thumb'
      src={previewThumbSource}
      alt={t('preview')}
    />

    <VideoPlayer
      segment_data={[]}
      encoded={false}
      url=''
      minimized={false}
      videoId={item.id}
      duration={item.duration}
      isActive={false}
      showControls={true}
      controlsDisabled={true}
    />

    <div className='post__cta'>
      {renderBuyButton()}
    </div>
  </MediaContainer>
) : (
                          <MediaContainer $locked={true}>
                            <VideoPlayer
                              segment_data={[]}
                              poster={thumbnailSource ?? undefined}
                              encoded={false}
                              url=''
                              minimized={false}
                              videoId={item.id}
                              duration={item.duration}
                              isActive={!!isInViewport && index === activeSlide}
                              showControls={true}
                              controlsDisabled={true}
                            />
                            <LockedContent>
                              <img src={premiumLock} alt='' />
                              <p className='locked-premium-text'>{t('premium')}</p>
                            </LockedContent>
                            <div className='post__cta post__cta--locked-preview'>{renderBuyButton()}</div>
                          </MediaContainer>
                        )}
                        {isLastPost &&
                          !hasNextPage && (
                            <LastPostInfo />
                          )}
                      </div>
                    </SwiperSlide>
                  )
                }
                return null
              }
            )}
        </Swiper>
      </div>
    </>
  )
}
export default SinglePostPreviewPremiumMedia
const MediaContainer = styled.div<{
  $locked?: boolean
}>`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${props =>
    props.$locked
      ? 'linear-gradient(157.59deg, #181920 0%, #0f0f13 100%)'
      : 'black'};
  .preview__background {
    position: absolute;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(24px)
      brightness(0.7);
    transform: scale(1.1);
    top: 0;
    left: 0;
    pointer-events: none;
    user-select: none;
  }
  .post__cta--locked-preview {
    z-index: 3;
  }

.preview__thumb {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 1;
}

`
const LockedContent = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: translateY(-5.5rem);
  pointer-events: none;
  img {
    display: block;
    width: 11rem;
    height: 11rem;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
  }
  .locked-premium-text {
    margin: 0.5rem 0 0;
    font-size: 1.6rem;
    font-weight: 400;
    opacity:0.6;
    line-height: 1;
    color: #fff;
  }
`
const LockedMinimized = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    157.59deg,
    #181920 0%,
    #0f0f13 100%
  );
  img {
    width: 4rem;
    height: 4rem;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
  }
`
